<script setup lang="ts">
/**
 * Колонки доски — макет 24:2: чипы пресетов, карточка со строками 48 и
 * строка «Добавить колонку».
 *
 * Порядок, имя, цвет, категория и лимит правятся в черновике и уходят одной
 * кнопкой «Сохранить» — а вот пресет применяется сразу: он заменяет доску на
 * сервере целиком, и «Отменить» после него было бы нечем.
 */
import { AppButton, AppModal, AppSelect } from '@vinprop/design-system'
import { GripVertical, Plus, X } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import draggable from 'vuedraggable'

import InlinePicker from '@/components/InlinePicker.vue'

import { STATUS_CATEGORIES, type SettingsColumn } from '../api'
import { useSettingsStore } from '../store'
import ColorPicker from './ColorPicker.vue'

import type {
  TrackerColumnPreset,
  V1StatusCategory,
  V1TypeProfile,
} from '@/api/clients/tracker-project'

const { t } = useI18n()
const store = useSettingsStore()

/**
 * Пресет и профиль типов — одна и та же развилка проекта: у разработки шесть
 * колонок и пять типов задач, у простого проекта три и два. Подсвечиваем чип
 * по профилю: своего поля «какой пресет применён» у проекта нет, да и колонки
 * с тех пор могли поправить руками.
 */
const PRESETS: { preset: TrackerColumnPreset; profile: V1TypeProfile }[] = [
  { preset: 'COLUMN_PRESET_DEVELOPMENT', profile: 'TYPE_PROFILE_DEVELOPMENT' },
  { preset: 'COLUMN_PRESET_SIMPLE', profile: 'TYPE_PROFILE_SIMPLE' },
]

const emit = defineEmits<{ failed: [error: unknown] }>()

const categories = computed(() =>
  STATUS_CATEGORIES.map((value) => ({ value, label: t(`settings.columns.categories.${value}`) })),
)

/**
 * Пресет ложится только на пустую доску: он сносит колонки, и задачам некуда
 * было бы деться. Чип гаснет заранее — иначе единственный способ узнать это
 * было бы нажать и получить отказ.
 */
const canPreset = computed(() => store.issueTotal === 0)

const usePreset = async (preset: TrackerColumnPreset) => {
  try {
    await store.usePreset(preset)
  } catch (failure) {
    emit('failed', failure)
  }
}

/** Колонка, которую убирают, и куда переложить её задачи */
const removing = ref<{ index: number; column: SettingsColumn } | null>(null)
const moveTo = ref('')

const moveTargets = computed(() =>
  store.columns
    .filter((column) => column.id && column.id !== removing.value?.column.id)
    .map((column) => ({ value: column.id, label: column.name })),
)

const remove = (index: number) => {
  const column = store.columns[index]

  if (!column) return

  // Пустую колонку убираем молча; про непустую спрашиваем сейчас, пока на
  // экране ещё видно, сколько в ней задач.
  if (!column.issueCount) {
    store.removeColumn(index)

    return
  }

  removing.value = { index, column }
  moveTo.value = moveTargets.value[0]?.value ?? ''
}

const confirmRemove = () => {
  if (!removing.value) return

  store.removeColumn(removing.value.index, moveTo.value)
  removing.value = null
}

/** Новая колонка берёт следующий цвет палитры — чтобы не совпасть с соседней */
const addColumn = () => {
  store.addColumn(t('settings.columns.newName'), '')
}

const onWip = (index: number, value: string) => {
  const limit = Number(value.replace(/\D/g, ''))

  store.patchColumn(index, { wipLimit: Number.isFinite(limit) ? limit : 0 })
}
</script>

<template>
  <div class="ctab">
    <div class="ctab__presets">
      <span class="ctab__presets-label">{{ t('settings.columns.preset') }}</span>

      <button
        v-for="entry in PRESETS"
        :key="entry.preset"
        type="button"
        class="ctab__chip"
        :class="{ 'ctab__chip--active': store.form.typeProfile === entry.profile }"
        :disabled="!canPreset"
        :title="canPreset ? undefined : t('settings.columns.presetLocked')"
        @click="usePreset(entry.preset)"
      >
        {{ t(`settings.columns.presets.${entry.preset}`) }}
      </button>
    </div>

    <p class="ctab__hint">
      {{ canPreset ? t('settings.columns.hint') : t('settings.columns.presetLocked') }}
    </p>

    <div class="ctab__card">
      <!-- Перетаскивание правит массив стора на месте — отдельный обработчик
           порядка не нужен, а `handle` не даёт схватить строку за поле имени. -->
      <draggable :list="store.columns" item-key="id" handle=".crow__grip" :animation="180">
        <template #item="{ element, index }">
          <div class="crow">
            <GripVertical class="crow__grip" :size="14" />

            <ColorPicker
              :model-value="element.color"
              :label="t('settings.columns.color')"
              @update:model-value="store.patchColumn(index, { color: $event })"
            />

            <input
              class="crow__name"
              :value="element.name"
              :aria-label="t('settings.columns.name')"
              maxlength="40"
              @input="store.patchColumn(index, { name: ($event.target as HTMLInputElement).value })"
            />

            <InlinePicker
              :options="categories"
              :model-value="element.statusCategory"
              :empty-text="t('toolbar.filters.empty')"
              @update:model-value="
                store.patchColumn(index, { statusCategory: $event as V1StatusCategory })
              "
            >
              <template #trigger="{ toggle }">
                <button type="button" class="crow__cat" @click="toggle()">
                  {{ t(`settings.columns.categories.${element.statusCategory || 'STATUS_CATEGORY_OPEN'}`) }} ▾
                </button>
              </template>
            </InlinePicker>

            <span class="crow__spacer" />

            <label class="crow__wip">
              {{ t('settings.columns.wip') }}
              <input
                class="crow__wip-input"
                :value="element.wipLimit || ''"
                inputmode="numeric"
                placeholder="—"
                :aria-label="t('settings.columns.wipLabel', { name: element.name })"
                @input="onWip(index, ($event.target as HTMLInputElement).value)"
              />
            </label>

            <button
              type="button"
              class="crow__remove"
              :aria-label="t('settings.columns.remove', { name: element.name })"
              @click="remove(index)"
            >
              <X :size="14" />
            </button>
          </div>
        </template>
      </draggable>

      <button type="button" class="ctab__add" @click="addColumn">
        <Plus :size="14" />
        {{ t('settings.columns.add') }}
      </button>
    </div>

    <AppModal
      :model-value="!!removing"
      :title="t('settings.columns.moveTitle')"
      size="small"
      @update:model-value="removing = null"
    >
      <p class="ctab__move-text">
        {{ t('settings.columns.moveText', { count: removing?.column.issueCount ?? 0 }) }}
      </p>

      <AppSelect
        v-model="moveTo"
        :options="moveTargets"
        :label="t('settings.columns.moveTarget')"
        size="small"
      />

      <template #footer>
        <AppButton variant="secondary" @click="removing = null">
          {{ t('common.actions.cancel') }}
        </AppButton>
        <AppButton variant="danger" :disabled="!moveTo" @click="confirmRemove">
          {{ t('settings.columns.moveSubmit') }}
        </AppButton>
      </template>
    </AppModal>
  </div>
</template>

<style scoped>
.ctab {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.ctab__presets {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ctab__presets-label {
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
}

.ctab__chip {
  padding: 5px 12px;
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-full);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

.ctab__chip--active {
  border-color: transparent;
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
}

.ctab__chip:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.ctab__hint {
  margin: -12px 0 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.ctab__card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: var(--color-surface);
  overflow: hidden;
}

.crow {
  display: flex;
  align-items: center;
  gap: 14px;
  height: 48px;
  padding: 0 16px;
  border-top: 1px solid var(--color-border);
}

/* Первая строка стоит вплотную к рамке карточки: линия над ней была бы второй */
.crow:first-child {
  border-top: none;
}

.crow__grip {
  flex-shrink: 0;
  color: var(--color-text-muted);
  cursor: grab;
}

.crow__name {
  width: 180px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
  outline: none;
}

.crow__name:focus {
  border-bottom: 1px solid var(--color-primary);
}

.crow__cat {
  padding: 5px 10px;
  border: none;
  border-radius: 7px;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  cursor: pointer;
}

.crow__spacer {
  flex: 1;
}

.crow__wip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
}

.crow__wip-input {
  width: 28px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  text-align: center;
  outline: none;
}

.crow__wip-input:focus {
  color: var(--color-text-primary);
}

.crow__remove {
  display: inline-flex;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.crow__remove:hover {
  color: var(--color-danger);
}

.ctab__add {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 16px;
  border: none;
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-muted);
  color: var(--color-primary);
  font-family: inherit;
  font-size: var(--font-size-body-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

.ctab__move-text {
  margin: 0 0 16px;
  color: var(--color-text-secondary);
  font-size: var(--font-size-body-sm);
}
</style>
